/* eslint-disable react/destructuring-assignment */
/* eslint-disable no-nested-ternary */

import type { ContractStatusEnum, WorkOrderStatusEnum, WorkOrderVersionActKc2VersionData } from 'gql/graphql';
import { ActKc2StatusEnum, DeviationEnum } from 'gql/graphql';
import { formatActTitle } from 'pages/Project/Common/ActKc2Title/ActKc2Title';
import { ColoredColumnTitle, DeviatedCell, DeviatedColumnTitle } from 'utils/tableCells';

import React, { useMemo } from 'react';

import { TitleActKC2Columns } from '../../WorkOrder/WorkOrderPrice/TitleActKC2Columns';

interface IGetCostRenderCellProps {
    key: string;
    deviationKey: string;
}

// eslint-disable-next-line react/display-name
const getCostRenderCell = (props: IGetCostRenderCellProps) => (row: any) => {
    const { key, deviationKey } = props;
    const isAct = typeof row.id === 'number';

    if (!isAct) {
        return null;
    }

    return row[deviationKey] === DeviationEnum.Up ? <DeviatedCell>{row[key]}</DeviatedCell> : row[key];
};

export const useWorkOrderVersionOthersActColumns = (
    acts: WorkOrderVersionActKc2VersionData[],
    reloadHandler: () => void,
    contractStatus?: ContractStatusEnum,
    workOrderStatus?: WorkOrderStatusEnum,
    isDisabledByProjectStatus?: boolean,
) =>
    useMemo(() => {
        let isOddSeason = true;
        const currentSeasonId = acts?.[0]?.seasonId;

        return acts.map((item) => {
            const { id, number, date, seasonId, status } = item;
            const isStatusAccepted = status === ActKc2StatusEnum.Accepted;

            if (seasonId !== currentSeasonId) {
                isOddSeason = !isOddSeason;
            }

            const isNotAcceptedActStatusColumns = !isStatusAccepted
                ? [
                      {
                          title: (
                              <ColoredColumnTitle isOdd={isOddSeason} align="right">
                                  Объем (план)
                              </ColoredColumnTitle>
                          ),
                          accessor: `actKC2CostVolumePlan_${id}`,
                          width: 112,
                      },
                      {
                          title: (
                              <ColoredColumnTitle isOdd={isOddSeason} align="right">
                                  <DeviatedColumnTitle
                                      title="Стоимость (план)"
                                      deviation={DeviationEnum.None}
                                      tooltipText="Превышает остаток по сезону"
                                  />
                              </ColoredColumnTitle>
                          ),
                          accessor: `actKC2PlanCost_${id}`,
                          width: 112,
                          renderCell: getCostRenderCell({
                              key: `actKC2PlanCost_${id}`,
                              deviationKey: `actKC2PlanCostDeviation_${id}`,
                          }),
                      },
                  ]
                : [];

            const baseActColumns = [
                {
                    title: (
                        <ColoredColumnTitle isOdd={isOddSeason} align="left">
                            Объем (согласовано)
                        </ColoredColumnTitle>
                    ),
                    accessor: `actKC2CostVolumeAccepted_${id}`,
                    width: 112,
                },
                {
                    title: (
                        <ColoredColumnTitle isOdd={isOddSeason} align="right">
                            <DeviatedColumnTitle
                                title="Стоимость (согласовано)"
                                deviation={DeviationEnum.None}
                                tooltipText="Превышает остаток по сезону"
                            />
                        </ColoredColumnTitle>
                    ),
                    accessor: `actKC2AcceptedCost_${id}`,
                    width: 135,
                    renderCell: getCostRenderCell({
                        key: `actKC2AcceptedCost_${id}`,
                        deviationKey: `actKC2AcceptedCostDeviation_${id}`,
                    }),
                },
            ];

            return {
                id,
                seasonId,
                title: (
                    <ColoredColumnTitle isOdd={isOddSeason} align="left">
                        <TitleActKC2Columns
                            id={id}
                            number={number}
                            status={status}
                            date={date}
                            contractStatus={contractStatus}
                            workOrderStatus={workOrderStatus}
                            onRefresh={reloadHandler}
                            isDisabledByProjectStatus={isDisabledByProjectStatus}
                            canCreateCorrection
                        />
                    </ColoredColumnTitle>
                ),
                accessor: formatActTitle(number, date),
                withoutPadding: true,
                columns: [...isNotAcceptedActStatusColumns, ...baseActColumns],
            };
        });
    }, [acts, contractStatus, workOrderStatus, reloadHandler, isDisabledByProjectStatus]);
